import { z } from "zod"

export const currencyPolicyMetricSchema = z.enum(["flights", "hours"])

export const currencyPolicyRecordSchema = z.object({
    id: z.uuid(),
    org_id: z.uuid(),
    name: z.string(),
    metric: currencyPolicyMetricSchema,
    threshold: z.coerce.number().positive(),
    window_days: z.coerce.number().int().positive(),
    warning_days: z.coerce.number().int().nonnegative(),
    drone_types: z.array(z.string()),
    email_reminder: z.boolean(),
    notify_all_users: z.boolean(),
    created_at: z.string(),
    created_by: z.guid(),
    updated_at: z.string().nullable(),
    updated_by: z.guid().nullable(),
})

export const currencyPolicyUserRecordSchema = z.object({
    policy_id: z.uuid(),
    user_id: z.guid(),
    org_id: z.uuid(),
    assigned_at: z.string(),
})

export const currencyPolicyRecipientRecordSchema = z.object({
    id: z.uuid(),
    policy_id: z.uuid(),
    org_id: z.uuid(),
    user_id: z.guid().nullable(),
    email: z.string().nullable(),
})

export const currencyPolicySchema = currencyPolicyRecordSchema.transform((r) => ({
    id: r.id,
    orgId: r.org_id,
    name: r.name,
    metric: r.metric,
    threshold: r.threshold,
    windowDays: r.window_days,
    warningDays: r.warning_days,
    droneTypes: r.drone_types,
    emailReminder: r.email_reminder,
    notifyAllUsers: r.notify_all_users,
    createdAt: r.created_at,
    createdBy: r.created_by,
    updatedAt: r.updated_at,
    updatedBy: r.updated_by,
}))

export const currencyPolicyUserSchema = currencyPolicyUserRecordSchema.transform((r) => ({
    policyId: r.policy_id,
    userId: r.user_id,
    orgId: r.org_id,
    assignedAt: r.assigned_at,
}))

export const currencyPolicyRecipientSchema = currencyPolicyRecipientRecordSchema.transform((r) => ({
    id: r.id,
    policyId: r.policy_id,
    orgId: r.org_id,
    userId: r.user_id,
    email: r.email,
}))

export const currencyPolicyRecipientInputSchema = z.union([
    z.object({ user_id: z.guid() }),
    z.object({ email: z.email() }),
])

export const creatableCurrencyPolicySchema = currencyPolicyRecordSchema
    .omit({
        id: true,
        org_id: true,
        created_at: true,
        created_by: true,
        updated_at: true,
        updated_by: true,
    })
    .extend({
        user_ids: z.array(z.guid()).default([]),
        recipients: z.array(currencyPolicyRecipientInputSchema).default([]),
    })

export const updatableCurrencyPolicySchema = creatableCurrencyPolicySchema.partial()

export const currencyPolicyAssignmentRowSchema = z.object({
    policy_id: z.uuid(),
    org_id: z.uuid(),
    name: z.string(),
    metric: currencyPolicyMetricSchema,
    threshold: z.coerce.number().positive(),
    window_days: z.coerce.number().int().positive(),
    warning_days: z.coerce.number().int().nonnegative(),
    drone_types: z.array(z.string()),
    notify_all_users: z.boolean(),
    user_id: z.guid(),
    assigned_at: z.string(),
})

export const currencyUserFlightRowSchema = z.object({
    user_id: z.guid(),
    started_at: z.string(),
    airtime: z.coerce.number().nullable(),
    manufacturer: z.string().nullable(),
    model: z.string().nullable(),
})

export const currencyPolicyRecipientRowSchema = z.object({
    policy_id: z.uuid(),
    user_id: z.guid().nullable(),
    email: z.string().nullable(),
})

export type CurrencyPolicyMetric = z.infer<typeof currencyPolicyMetricSchema>
export type CurrencyPolicyRecord = z.infer<typeof currencyPolicyRecordSchema>
export type CurrencyPolicyUserRecord = z.infer<typeof currencyPolicyUserRecordSchema>
export type CurrencyPolicyRecipientRecord = z.infer<typeof currencyPolicyRecipientRecordSchema>
export type CurrencyPolicy = z.infer<typeof currencyPolicySchema>
export type CurrencyPolicyUser = z.infer<typeof currencyPolicyUserSchema>
export type CurrencyPolicyRecipient = z.infer<typeof currencyPolicyRecipientSchema>
export type CurrencyPolicyRecipientInput = z.infer<typeof currencyPolicyRecipientInputSchema>
export type CreatableCurrencyPolicy = z.infer<typeof creatableCurrencyPolicySchema>
export type UpdatableCurrencyPolicy = z.infer<typeof updatableCurrencyPolicySchema>
export type CurrencyPolicyAssignmentRow = z.infer<typeof currencyPolicyAssignmentRowSchema>
export type CurrencyUserFlightRow = z.infer<typeof currencyUserFlightRowSchema>
export type CurrencyPolicyRecipientRow = z.infer<typeof currencyPolicyRecipientRowSchema>
export type CurrencyNotificationKind = "warning" | "overdue"
